import { useState } from "react";
import { Heart, Star, Calendar, MessageCircle, Send, TrendingUp } from "lucide-react";

const TABS = [
  { id: "thanks", label: "Thank-yous", icon: Heart },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "rebook", label: "Rebooking", icon: Calendar },
  { id: "winback", label: "Win-back", icon: MessageCircle },
];

const CONTENT = {
  thanks: {
    subject: "Thanks for coming in today",
    metric: "94% open rate",
    lines: [
      "Hi Maya, it was so lovely seeing you today. We hope the balayage feels exactly right by the time you get home.",
      "— Bloom & Balm",
    ],
    inbox: [
      { name: "Maya P.", note: "Thank-you sent · 2 min after visit", tag: "opened" },
      { name: "Dev R.", note: "Thank-you sent · 4 min after visit", tag: "opened" },
      { name: "Sara L.", note: "Thank-you queued for tomorrow 10:00", tag: "queued" },
    ],
  },
  reviews: {
    subject: "Would you share a quick review?",
    metric: "1 in 3 leaves a 5-star review",
    lines: [
      "Loved your visit? A one-line Google review means the world to a small studio like ours.",
      "Tap the stars below — takes 10 seconds.",
    ],
    inbox: [
      { name: "Jordan K.", note: "★★★★★ · Google · yesterday", tag: "5-star" },
      { name: "Priya S.", note: "★★★★★ · Google · yesterday", tag: "5-star" },
      { name: "Owen T.", note: "Review request sent · pending", tag: "pending" },
    ],
  },
  rebook: {
    subject: "Time for your next visit?",
    metric: "+38% rebooking rate",
    lines: [
      "It's been about 5 weeks since your last cut, Alex. Tuesday 4:30 or Thursday 11:00 are open with Sam.",
      "Reply 1 for Tue, 2 for Thu.",
    ],
    inbox: [
      { name: "Alex M.", note: "Rebooked Thu 11:00 · $85", tag: "booked" },
      { name: "Riley B.", note: "Rebooked Sat 2:00 · $120", tag: "booked" },
      { name: "Casey Q.", note: "Nudge sent · awaiting reply", tag: "pending" },
    ],
  },
  winback: {
    subject: "We miss you",
    metric: "22% of lapsed guests return",
    lines: [
      "It's been a minute, Jamie. Here's 15% off your next appointment — on us, no expiry.",
      "Book anytime in the next 30 days.",
    ],
    inbox: [
      { name: "Jamie F.", note: "Win-back sent · offer viewed", tag: "opened" },
      { name: "Nina H.", note: "Booked with offer · $95", tag: "booked" },
      { name: "Theo D.", note: "Segmented: lapsed 90+ days", tag: "queued" },
    ],
  },
};

export default function DashboardPreview() {
  const [active, setActive] = useState("thanks");
  const data = CONTENT[active];
  const ActiveIcon = TABS.find(t => t.id === active).icon;

  return (
    <div className="relative" data-testid="dashboard-preview">
      <div className="rounded-3xl bg-white border border-[#E5E0D8] shadow-[0_20px_40px_-12px_rgba(24,61,54,0.15)] overflow-hidden">
        {/* Chrome bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E0D8] bg-[#FAF7F0]/60">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#F4B183]/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#A8BFA3]/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#183D36]/40" />
          </div>
          <div className="text-xs font-accent text-[#4A4A4A]">example · app.bookyournextvisit.com</div>
          <div className="w-10" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-2 border-b border-[#E5E0D8] overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = active === t.id;
            return (
              <button
                key={t.id}
                data-testid={`dashboard-tab-${t.id}`}
                onClick={() => setActive(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                  isActive
                    ? "bg-[#183D36] text-[#FAF7F0]"
                    : "text-[#252525] hover:bg-[#EFECE5]"
                }`}
              >
                <Icon size={14} strokeWidth={1.75} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 sm:grid-cols-5 min-h-[380px]">
          {/* Inbox column */}
          <div className="sm:col-span-2 border-r border-[#E5E0D8] bg-[#FAF7F0]/50">
            <div className="px-4 py-3 flex items-center justify-between">
              <span className="overline text-[10px]">Today's queue</span>
              <span className="text-xs text-[#4A4A4A]">{data.inbox.length}</span>
            </div>
            <ul className="divide-y divide-[#E5E0D8]">
              {data.inbox.map((row, i) => (
                <li key={i} className="px-4 py-3 hover:bg-white/70 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm text-[#183D36]">{row.name}</span>
                    <span className={`text-[10px] font-accent tracking-wide uppercase px-2 py-0.5 rounded-full ${
                      row.tag === "booked" ? "bg-[#A8BFA3] text-[#183D36]" :
                      row.tag === "5-star" ? "bg-[#F4B183] text-[#183D36]" :
                      row.tag === "opened" ? "bg-[#183D36] text-[#FAF7F0]" :
                      "bg-[#EFECE5] text-[#252525]"
                    }`}>{row.tag}</span>
                  </div>
                  <p className="text-xs text-[#4A4A4A] mt-1">{row.note}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* Message preview */}
          <div className="sm:col-span-3 p-6 flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-full bg-[#A8BFA3] text-[#183D36] flex items-center justify-center">
                <ActiveIcon size={16} strokeWidth={1.75} />
              </div>
              <div>
                <div className="font-heading text-sm text-[#183D36]">{data.subject}</div>
                <div className="text-[10px] font-accent uppercase tracking-widest text-[#F4B183]">Auto-scheduled</div>
              </div>
            </div>

            <div className="rounded-2xl bg-[#FAF7F0] border border-[#E5E0D8] p-4 space-y-2 text-sm text-[#252525] leading-relaxed flex-1">
              {data.lines.map((l, i) => <p key={i}>{l}</p>)}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-[#183D36] font-medium">
                <TrendingUp size={16} className="text-[#F4B183]" />
                {data.metric}
              </div>
              <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#183D36] text-[#FAF7F0] text-xs font-medium hover:bg-[#122e29] transition-colors">
                <Send size={12} /> Send now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Floating stat card */}
      <div className="hidden sm:block absolute -bottom-6 -left-6 rounded-2xl bg-[#183D36] text-[#FAF7F0] p-4 shadow-xl w-56">
        <div className="overline text-[#F4B183] text-[10px] mb-1">This month</div>
        <div className="font-heading text-2xl">$24,180</div>
        <div className="text-xs opacity-70">Recovered revenue from rebookings</div>
      </div>
    </div>
  );
}
